import { useEffect, useState } from 'react';

/**
 * Height (px) currently occluded at the bottom by the on-screen keyboard, derived from the
 * VisualViewport API. Mobile browsers — iOS Safari especially — don't shrink the layout viewport
 * when the keyboard opens, so a bottom-anchored `position: fixed` surface (a drawer/sheet) sits
 * behind the keyboard. Reserve this inset at the bottom of such a surface to keep its footer/controls
 * above the keyboard.
 *
 * Returns 0 where VisualViewport is unavailable (older browsers / SSR) or the keyboard is closed,
 * so callers can use it unconditionally — desktop simply gets 0.
 */
export const useKeyboardInset = (): number => {
	const [inset, setInset] = useState(0);

	useEffect(() => {
		const viewport = window.visualViewport;
		if (!viewport) return;

		const update = () => {
			// Gap between the layout-viewport bottom and the visual-viewport bottom is the keyboard
			// (plus any collapsing bottom browser chrome). offsetTop covers the page being scrolled
			// within the visual viewport. Clamp negatives to 0 for the keyboard-closed resting state.
			const next = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
			setInset(next);
		};

		update();
		viewport.addEventListener('resize', update);
		viewport.addEventListener('scroll', update);
		return () => {
			viewport.removeEventListener('resize', update);
			viewport.removeEventListener('scroll', update);
		};
	}, []);

	return inset;
};
